
import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { white } from '@zocdoc/branding/colors';
import { breakpoint } from '@zocdoc/components/src/grid';
import Lazyload from '@zocdoc/components/src/LazyLoad';
import FallbackImage from './FallbackImage.svg';
import {
    PHOTO_SIZE_LARGE,
    PHOTO_SIZE_MEDIUM,
    PHOTO_SIZE_XXSMALL,
} from '../constants';

export class DoctorPhoto extends React.PureComponent {
    static propTypes = {
        isProduction: PropTypes.bool,
        provider: PropTypes.shape({
            circlePictureUrl: PropTypes.string,
            nameInSentence: PropTypes.string,
            squarePictureUrl: PropTypes.string,
        }),
    };

    render() {
        return (
            <Wrapper>
                {this.renderPhoto()}
                {this.props.children}
            </Wrapper>
        );
    }

    renderPhoto() {
        const {
            isProduction,
            provider,
        } = this.props;

        if (provider != null && (provider.circlePictureUrl || provider.squarePictureUrl)) {
            if (!provider.circlePictureUrl || (provider.circlePictureUrl === provider.squarePictureUrl)) {
                return (
                    <CircleMask data-test="doctor-card-photo-mask">
                        {this.renderPicture(provider.nameInSentence, provider.squarePictureUrl, isProduction)}
                    </CircleMask>
                );
            }

            return this.renderPicture(provider.nameInSentence, provider.circlePictureUrl, isProduction);
        }

        return this.renderFallbackImage(provider);
    }

    renderFallbackImage(provider) {
        const altText = provider ? provider.nameInSentence : 'doctor photo';
        return this.renderImage(altText, FallbackImage);
    }

    renderImage(altText, imageSource) {
        return (
            <Img
                alt={altText}
                data-test="doctor-card-photo-image"
                itemProp="image"
                src={imageSource}
            />
        );
    }

    renderPicture(altText, imageSource, isProduction) {
        return (
            <StyledLazyload>
                <picture>
                    {!isProduction ? <source
                        srcSet={`${imageSource}?&fm=webp`}
                        type="image/webp"
                    /> : null}
                    {this.renderImage(altText, imageSource)}
                </picture>
            </StyledLazyload>
        );
    }
}

const PHOTO_MARGIN_XXSMALL = 22;
const PHOTO_MARGIN_SMALL = 24;
const PHOTO_MARGIN_MEDIUM = 28;
const PHOTO_MARGIN_LARGE = 40;

/**
 * https://css-tricks.com/aspect-ratio-boxes/
 *
 * This allows us to adopt the parent's sizing without
 * having to hard code it over and over again.
 *
 * Specifically this gives a 1:1 "square" aspect ratio.
 */
const StyledLazyload = styled(Lazyload)`
    width: 100%;
    display: block;
    position: relative;

    &::before {
        content: '';
        display: block;
        float: left;
        margin-left: -1px;
        padding-top: 100%;
        width: 1px;
    }

    &::after {
        clear: both;
        content: '';
        display: table;
    }
`;

export const PhotoContainer = styled.div`
    align-items: center;
    display: flex;
    flex-direction: column;
    flex: 0 0 ${PHOTO_SIZE_XXSMALL}px;
    margin-right: ${PHOTO_MARGIN_XXSMALL}px;

    ${breakpoint.small} {
        margin-right: ${PHOTO_MARGIN_SMALL}px;
    }

    ${breakpoint.medium} {
        flex-basis: ${PHOTO_SIZE_MEDIUM}px;
        margin-right: ${PHOTO_MARGIN_MEDIUM}px;
    }

    ${breakpoint.large} {
        flex-basis: ${PHOTO_SIZE_LARGE}px;
        margin-right: ${PHOTO_MARGIN_LARGE}px;
    }
`;

const CircleMask = styled.div`
    align-items: center;
    border-color: ${white};
    border-radius: 50%;
    display: flex;
    overflow: hidden;

    height: ${PHOTO_SIZE_XXSMALL}px;
    width: ${PHOTO_SIZE_XXSMALL}px;

    ${breakpoint.medium} {
        height: ${PHOTO_SIZE_MEDIUM}px;
        width: ${PHOTO_SIZE_MEDIUM}px;
    }

    ${breakpoint.large} {
        height: ${PHOTO_SIZE_LARGE}px;
        width: ${PHOTO_SIZE_LARGE}px;
    }
`;

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
`;

const Img = styled.img`
    animation: 0.3s ${fadeIn} ease-out;

    width: ${PHOTO_SIZE_XXSMALL}px;

    ${breakpoint.medium} {
        width: ${PHOTO_SIZE_MEDIUM}px;
    }

    ${breakpoint.large} {
        width: ${PHOTO_SIZE_LARGE}px;
    }
`;

const Wrapper = styled.div`
    position: relative;
`;
